import { useEffect, useRef, useState } from 'react';
import { getBatteryDiagnostic } from '../../services/wearerService';

/**
 * Diagnóstico de batería del reloj.
 *
 * Una sola llamada trae el veredicto y la serie del gráfico, así que la ficha no
 * puede mostrar un diagnóstico calculado sobre datos distintos de los que dibuja.
 *
 * Expone `loading` como booleano real en vez del toast que se cerraba solo a los
 * dos segundos, y distingue el error de la ausencia de datos.
 */
export default function useBatteryDiagnostic(tokens, wearer) {
    const [diagnostico, setDiagnostico] = useState(null);
    const [serie, setSerie] = useState([]);
    const [cobertura, setCobertura] = useState(null);
    const [sesiones, setSesiones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [consultado, setConsultado] = useState(false);

    // Número de la última consulta lanzada. Una respuesta que llega tarde (de un
    // reloj anterior o de una consulta repetida) no debe pisar lo que se ve.
    const ultimaConsulta = useRef(0);

    const deviceId = wearer?.deviceId;

    // La página no se desmonta al buscar otro reloj: sin esto la ficha seguía
    // mostrando el diagnóstico del anterior. Se vuelve al estado inicial y se
    // invalida cualquier consulta en vuelo.
    useEffect(() => {
        ultimaConsulta.current += 1;
        setDiagnostico(null);
        setSerie([]);
        setCobertura(null);
        setSesiones([]);
        setError(null);
        setLoading(false);
        setConsultado(false);
    }, [deviceId]);

    const diagnosticar = async () => {
        ultimaConsulta.current += 1;
        const consulta = ultimaConsulta.current;
        const esVigente = () => consulta === ultimaConsulta.current;

        if (!wearer?.deviceId) {
            setError('Este reloj no tiene deviceId, no se puede diagnosticar.');
            setConsultado(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await getBatteryDiagnostic(wearer.deviceId, tokens.AccessToken);
            if (!esVigente()) return;
            setDiagnostico(data.diagnostico);
            setSerie(data.serie || []);
            setCobertura(data.cobertura || null);
            setSesiones(data.sesiones || []);
        } catch (e) {
            if (!esVigente()) return;
            console.error('Error al diagnosticar la batería:', e);
            setDiagnostico(null);
            setSerie([]);
            setCobertura(null);
            setSesiones([]);
            setError(
                e?.response?.status === 401
                    ? 'La sesión expiró. Vuelve a iniciar sesión e intenta de nuevo.'
                    : 'No se pudo obtener el diagnóstico. Intenta de nuevo en unos segundos.'
            );
        } finally {
            if (esVigente()) {
                setLoading(false);
                setConsultado(true);
            }
        }
    };

    return {
        diagnostico,
        serie,
        cobertura,
        sesiones,
        loading,
        error,
        consultado,
        diagnosticar,
    };
}
